import { useEffect, useRef, useState } from "react";
import type { Control, FieldErrors, UseFormWatch } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { Mail, Phone } from "lucide-react";
import {
  Button,
  Input,
  OtpInput,
  ToggleGroup,
  Label,
  FieldError,
  fieldErrorId,
} from "@/components/ui";
import { PhoneInput } from "@/components/ui/phone";
import type { UseFormRegister } from "react-hook-form";
import { apiErrorName, isApiClientError } from "@/lib/api";
import { toastApiError } from "@/lib/errorMessages";
import { DEFAULT_LOCALE, supportedLocaleSelectOptions } from "@/lib/locales";
import { countryFromE164, resolveDefaultPhoneCountry } from "@/lib/phoneDefaults";
import {
  legalInfoQueryKey,
  profileService,
  type LegalFieldPolicy,
  type UserLegalInfo,
} from "@/services/profileService";
import { cn } from "@/lib/utils";
import {
  ContactVerifyChannel,
  ContactVerifyUiKind,
} from "./contactVerify";
import type { ProfileFormValues } from "./profileFormTypes";
import { isFieldExcluded } from "./profileFormTypes";
import type { ProfileSectionControls } from "./profileSectionTypes";
import { ProfileSectionCard } from "./ProfileSectionCard";
import { ContactVerifyStatus } from "@/components/ui/verifyStatus";

/** Poll profile after magic-link send so cross-device verify updates this tab. */
const EMAIL_VERIFY_POLL_INTERVAL_MS = 5_000;
const EMAIL_VERIFY_POLL_DURATION_MS = 3 * 60 * 1_000;

type VerifyUi =
  | { kind: typeof ContactVerifyUiKind.Idle }
  | {
      kind: typeof ContactVerifyUiKind.Sent;
      channel: ContactVerifyChannel;
      code: string;
      codeError?: string;
    };

interface ContactSectionProps {
  control: Control<ProfileFormValues>;
  register: UseFormRegister<ProfileFormValues>;
  watch: UseFormWatch<ProfileFormValues>;
  errors?: FieldErrors<ProfileFormValues>;
  userId: string;
  savedPhone: string;
  savedEmail: string;
  phoneDirty: boolean;
  emailDirty: boolean;
  phoneVerified?: boolean;
  emailVerified?: boolean;
  fieldPolicy: LegalFieldPolicy;
  section: ProfileSectionControls;
  onContactVerified: (profile: UserLegalInfo) => void;
}

export function ContactSection({
  control,
  register,
  watch,
  errors,
  userId,
  savedPhone,
  savedEmail,
  phoneDirty,
  emailDirty,
  phoneVerified,
  emailVerified,
  fieldPolicy,
  section,
  onContactVerified,
}: ContactSectionProps) {
  const { t, i18n } = useTranslation("settings");

  const showPec = !isFieldExcluded(fieldPolicy, "pec");

  const defaultCountry = resolveDefaultPhoneCountry({
    registeredCountry: watch("registered_country"),
    language: i18n.language,
  });
  const preferredLanguage = watch("preferred_language") || DEFAULT_LOCALE;

  const notificationChannelGroupId = "profile-notification-channel";
  const notificationChannelErrorId = fieldErrorId(notificationChannelGroupId);
  const preferredLanguageGroupId = "profile-preferred-language";
  const preferredLanguageErrorId = fieldErrorId(preferredLanguageGroupId);

  const [verifyUi, setVerifyUi] = useState<VerifyUi>({
    kind: ContactVerifyUiKind.Idle,
  });
  const [sendBlocked, setSendBlocked] = useState<
    Record<ContactVerifyChannel, boolean>
  >({
    [ContactVerifyChannel.Phone]: false,
    [ContactVerifyChannel.Email]: false,
  });
  const [busy, setBusy] = useState<"send" | "confirm" | null>(null);
  const [emailPollDeadlineMs, setEmailPollDeadlineMs] = useState<number | null>(
    null,
  );
  const emailPollSuccessHandled = useRef(false);

  useEffect(() => {
    setVerifyUi({ kind: ContactVerifyUiKind.Idle });
    setSendBlocked((prev) => ({
      ...prev,
      [ContactVerifyChannel.Phone]: false,
    }));
    setBusy(null);
  }, [savedPhone]);

  useEffect(() => {
    setVerifyUi({ kind: ContactVerifyUiKind.Idle });
    setSendBlocked((prev) => ({
      ...prev,
      [ContactVerifyChannel.Email]: false,
    }));
    setEmailPollDeadlineMs(null);
    emailPollSuccessHandled.current = false;
    setBusy(null);
  }, [savedEmail]);

  useEffect(() => {
    if (emailPollDeadlineMs == null) return;
    const remainingMs = emailPollDeadlineMs - Date.now();
    if (remainingMs <= 0) {
      setEmailPollDeadlineMs(null);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setEmailPollDeadlineMs(null);
    }, remainingMs);
    return () => window.clearTimeout(timeoutId);
  }, [emailPollDeadlineMs]);

  const phoneCanSend =
    !phoneVerified &&
    !phoneDirty &&
    savedPhone.trim().length > 0 &&
    !sendBlocked[ContactVerifyChannel.Phone];

  const emailCanSend =
    !emailVerified &&
    !emailDirty &&
    savedEmail.trim().length > 0 &&
    !sendBlocked[ContactVerifyChannel.Email];

  const emailLinkPending =
    verifyUi.kind === ContactVerifyUiKind.Sent &&
    verifyUi.channel === ContactVerifyChannel.Email &&
    !emailVerified &&
    emailPollDeadlineMs != null;

  const { data: polledLegalInfo } = useQuery({
    queryKey: legalInfoQueryKey(userId),
    queryFn: () => profileService.getProfile(userId),
    enabled: emailLinkPending,
    staleTime: 0,
    refetchInterval: (query) => {
      if (!emailLinkPending) return false;
      if (
        emailPollDeadlineMs != null &&
        Date.now() >= emailPollDeadlineMs
      ) {
        return false;
      }
      if (query.state.data?.contact_email_verified) return false;
      return EMAIL_VERIFY_POLL_INTERVAL_MS;
    },
  });

  useEffect(() => {
    if (
      !emailLinkPending ||
      !polledLegalInfo?.contact_email_verified ||
      emailPollSuccessHandled.current
    ) {
      return;
    }
    emailPollSuccessHandled.current = true;
    toast.success(t("forms.contact.verifyEmailSuccess"));
    setVerifyUi({ kind: ContactVerifyUiKind.Idle });
    setEmailPollDeadlineMs(null);
    onContactVerified(polledLegalInfo);
  }, [emailLinkPending, onContactVerified, polledLegalInfo, t]);

  const handleSend = async (channel: ContactVerifyChannel) => {
    const canSend =
      channel === ContactVerifyChannel.Phone ? phoneCanSend : emailCanSend;
    if (!canSend || busy) return;
    setBusy("send");
    try {
      if (channel === ContactVerifyChannel.Phone) {
        await profileService.sendPhoneVerification(userId);
        toast.success(t("forms.contact.verifyPhoneSent"));
      } else {
        await profileService.sendEmailVerification(userId);
        toast.success(t("forms.contact.verifyEmailSent"));
        emailPollSuccessHandled.current = false;
        setEmailPollDeadlineMs(Date.now() + EMAIL_VERIFY_POLL_DURATION_MS);
      }
      setVerifyUi({ kind: ContactVerifyUiKind.Sent, channel, code: "" });
    } catch (error: unknown) {
      if (isApiClientError(error) && error.response?.status === 429) {
        setSendBlocked((prev) => ({ ...prev, [channel]: true }));
        toast.error(
          channel === ContactVerifyChannel.Phone
            ? t("forms.contact.verifyPhoneLimit")
            : t("forms.contact.verifyEmailLimit"),
        );
      } else if (
        channel === ContactVerifyChannel.Phone &&
        apiErrorName(error) === "PhoneNotSmsCapableError"
      ) {
        toast.error(t("forms.contact.verifyPhoneNotSmsCapable"));
      } else {
        toastApiError(error, "generic");
      }
    } finally {
      setBusy(null);
    }
  };

  const handleConfirmPhone = async (codeOverride?: string) => {
    if (
      verifyUi.kind !== ContactVerifyUiKind.Sent ||
      verifyUi.channel !== ContactVerifyChannel.Phone ||
      busy
    ) {
      return;
    }
    const trimmed = (codeOverride ?? verifyUi.code).trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setVerifyUi({
        kind: ContactVerifyUiKind.Sent,
        channel: ContactVerifyChannel.Phone,
        code: trimmed,
        codeError: t("forms.contact.verifyCodeInvalid"),
      });
      return;
    }
    setBusy("confirm");
    setVerifyUi({
      kind: ContactVerifyUiKind.Sent,
      channel: ContactVerifyChannel.Phone,
      code: trimmed,
      codeError: undefined,
    });
    try {
      const profile = await profileService.confirmPhoneVerification(
        userId,
        trimmed,
      );
      toast.success(t("forms.contact.verifyPhoneSuccess"));
      setVerifyUi({ kind: ContactVerifyUiKind.Idle });
      onContactVerified(profile);
    } catch (error: unknown) {
      if (apiErrorName(error) === "PhoneVerificationFailedError") {
        setVerifyUi({
          kind: ContactVerifyUiKind.Sent,
          channel: ContactVerifyChannel.Phone,
          code: trimmed,
          codeError: t("forms.contact.verifyCodeInvalid"),
        });
      } else if (isApiClientError(error) && error.response?.status === 429) {
        toast.error(t("forms.contact.verifyConfirmLimit"));
      } else {
        toastApiError(error, "generic");
      }
    } finally {
      setBusy(null);
    }
  };

  const phoneOtpOpen =
    verifyUi.kind === ContactVerifyUiKind.Sent &&
    verifyUi.channel === ContactVerifyChannel.Phone &&
    !phoneVerified;

  const emailLinkSent =
    verifyUi.kind === ContactVerifyUiKind.Sent &&
    verifyUi.channel === ContactVerifyChannel.Email &&
    !emailVerified;

  const renderVerifyButton = ({
    verified,
    dirty,
    savedEmpty,
    blocked,
    canSend,
    channel,
    pending,
  }: {
    verified: boolean;
    dirty: boolean;
    savedEmpty: boolean;
    blocked: boolean;
    canSend: boolean;
    channel: ContactVerifyChannel;
    pending: boolean;
  }) => {
    if (verified) {
      return (
        <span
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-success-600/25 bg-success-50 px-3 text-sm font-medium text-success-700"
          title={t("forms.contact.verified")}
        >
          {t("forms.contact.verified")}
        </span>
      );
    }

    if (dirty || savedEmpty) {
      return (
        <span className="inline-flex h-9 max-w-[10rem] shrink-0 items-center text-xs leading-snug text-surface-500">
          {t("forms.contact.verifySaveFirst")}
        </span>
      );
    }

    if (blocked) {
      return (
        <span className="inline-flex h-9 max-w-[10rem] shrink-0 items-center text-xs leading-snug text-surface-500">
          {channel === ContactVerifyChannel.Phone
            ? t("forms.contact.verifyPhoneLimit")
            : t("forms.contact.verifyEmailLimit")}
        </span>
      );
    }

    const label =
      channel === ContactVerifyChannel.Phone
        ? pending
          ? t("forms.contact.resendCode")
          : t("forms.contact.sendSmsCode")
        : pending
          ? t("forms.contact.resendLink")
          : t("forms.contact.sendEmailLink");

    return (
      <Button
        type="button"
        variant="outline"
        size="md"
        className="shrink-0"
        isLoading={busy === "send"}
        disabled={!canSend || busy === "send"}
        onClick={() => void handleSend(channel)}
      >
        {label}
      </Button>
    );
  };

  return (
    <ProfileSectionCard
      title={t("forms.contact.cardTitle")}
      icon={Phone}
      {...section}
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Controller
                name="contact_phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    label={t("forms.contact.phone")}
                    required
                    value={field.value}
                    onChange={field.onChange}
                    defaultCountry={
                      countryFromE164(field.value) ?? defaultCountry
                    }
                    placeholder={t("forms.contact.phonePlaceholder")}
                    error={errors?.contact_phone?.message}
                    verifyStatus={
                      phoneVerified
                        ? ContactVerifyStatus.Verified
                        : ContactVerifyStatus.Unverified
                    }
                  />
                )}
              />
            </div>
            {renderVerifyButton({
              verified: Boolean(phoneVerified),
              dirty: phoneDirty,
              savedEmpty: !savedPhone.trim(),
              blocked: sendBlocked[ContactVerifyChannel.Phone],
              canSend: phoneCanSend,
              channel: ContactVerifyChannel.Phone,
              pending: phoneOtpOpen,
            })}
          </div>
          {!errors?.contact_phone?.message && !phoneOtpOpen ? (
            <p className="mt-1 text-sm text-surface-500">
              {t("forms.contact.phoneHint")}
            </p>
          ) : null}
          {phoneOtpOpen && verifyUi.kind === ContactVerifyUiKind.Sent ? (
            <div className="mt-3 rounded-xl border border-primary-200 bg-primary-50/40 px-4 py-3">
              <p className="text-sm font-medium text-surface-800">
                {t("forms.contact.enterSmsCode")}
              </p>
              <div className="mt-3">
                <OtpInput
                  value={verifyUi.code}
                  autoFocus
                  disabled={busy === "confirm"}
                  error={verifyUi.codeError}
                  aria-label={t("forms.contact.otpAriaLabel")}
                  onChange={(code) => {
                    setVerifyUi({
                      kind: ContactVerifyUiKind.Sent,
                      channel: ContactVerifyChannel.Phone,
                      code,
                      codeError: undefined,
                    });
                    if (code.length === 6) {
                      void handleConfirmPhone(code);
                    }
                  }}
                />
              </div>
              <div className="mt-3">
                <Button
                  type="button"
                  size="sm"
                  isLoading={busy === "confirm"}
                  disabled={verifyUi.code.length !== 6 || busy === "confirm"}
                  onClick={() => void handleConfirmPhone()}
                >
                  {t("forms.contact.confirmCode")}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <div>
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Input
                {...register("contact_email")}
                label={t("forms.contact.email")}
                required
                type="email"
                placeholder={t("forms.contact.emailPh")}
                leftIcon={<Mail className="h-4 w-4" />}
                error={errors?.contact_email?.message}
                verifyStatus={
                  emailVerified
                    ? ContactVerifyStatus.Verified
                    : ContactVerifyStatus.Unverified
                }
              />
            </div>
            {renderVerifyButton({
              verified: Boolean(emailVerified),
              dirty: emailDirty,
              savedEmpty: !savedEmail.trim(),
              blocked: sendBlocked[ContactVerifyChannel.Email],
              canSend: emailCanSend,
              channel: ContactVerifyChannel.Email,
              pending: emailLinkSent,
            })}
          </div>
          {!errors?.contact_email?.message && !emailVerified && !emailLinkSent ? (
            <p className="mt-1 text-sm text-surface-500">
              {t("forms.contact.emailHint")}
            </p>
          ) : null}
          {emailLinkSent ? (
            <div className="mt-3 rounded-xl border border-primary-200 bg-primary-50/40 px-4 py-3">
              <p className="text-sm font-medium text-surface-800">
                {t("forms.contact.verifyEmailWaiting")}
              </p>
            </div>
          ) : null}
        </div>

        <div className="md:col-span-2">
          <Controller
            name="partner_notification_channel"
            control={control}
            render={({ field }) => (
              <div>
                <Label
                  as="p"
                  id={`${notificationChannelGroupId}-label`}
                  spacing="tight"
                >
                  {t("forms.contact.notificationChannel")}
                  <span className="text-error-500"> *</span>
                </Label>
                <p className="mb-3 text-sm text-surface-500">
                  {t("forms.contact.notificationChannelHint")}
                </p>
                <div
                  id={notificationChannelGroupId}
                  className="grid grid-cols-2 gap-4"
                  role="group"
                  aria-labelledby={`${notificationChannelGroupId}-label`}
                  aria-invalid={
                    errors?.partner_notification_channel ? "true" : undefined
                  }
                  aria-describedby={
                    errors?.partner_notification_channel
                      ? notificationChannelErrorId
                      : undefined
                  }
                >
                  {(
                    [
                      {
                        value: "WHATSAPP" as const,
                        title: t("forms.contact.notificationChannelWhatsApp"),
                      },
                      {
                        value: "EMAIL" as const,
                        title: t("forms.contact.notificationChannelEmail"),
                      },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => field.onChange(option.value)}
                      onBlur={field.onBlur}
                      className={cn(
                        "flex items-center gap-3 rounded-xl border p-4 transition-all",
                        field.value === option.value
                          ? "border-primary-500 bg-primary-50"
                          : "border-surface-200 hover:border-surface-300",
                      )}
                    >
                      {option.value === "WHATSAPP" ? (
                        <img
                          src="/icons/whatsapp.svg"
                          alt=""
                          aria-hidden
                          className="h-7 w-7 shrink-0"
                        />
                      ) : (
                        <Mail
                          className="h-7 w-7 shrink-0 text-primary-600"
                          aria-hidden
                        />
                      )}
                      <span className="font-semibold text-surface-900">
                        {option.title}
                      </span>
                    </button>
                  ))}
                </div>
                {errors?.partner_notification_channel?.message ? (
                  <FieldError id={notificationChannelErrorId}>
                    {errors.partner_notification_channel.message}
                  </FieldError>
                ) : null}
              </div>
            )}
          />
          <Controller
            name="preferred_language"
            control={control}
            render={({ field: languageField }) => (
              <div className="mt-4 border-t border-surface-100 pt-4">
                <Label
                  as="p"
                  id={`${preferredLanguageGroupId}-label`}
                  spacing="relaxed"
                >
                  {t("forms.contact.notificationLanguage")}
                  <span className="text-error-500"> *</span>
                </Label>
                <div
                  id={preferredLanguageGroupId}
                  role="group"
                  aria-labelledby={`${preferredLanguageGroupId}-label`}
                  aria-invalid={
                    errors?.preferred_language ? "true" : undefined
                  }
                  aria-describedby={
                    errors?.preferred_language
                      ? preferredLanguageErrorId
                      : undefined
                  }
                >
                  <ToggleGroup
                    value={preferredLanguage}
                    onChange={(code) => languageField.onChange(code)}
                    ariaLabel={t("forms.contact.notificationLanguage")}
                    size="sm"
                    options={supportedLocaleSelectOptions()}
                  />
                </div>
                {errors?.preferred_language?.message ? (
                  <FieldError id={preferredLanguageErrorId}>
                    {errors.preferred_language.message}
                  </FieldError>
                ) : null}
              </div>
            )}
          />
        </div>
        {showPec ? (
          <div className="md:col-span-2">
            <Input
              {...register("pec")}
              label={t("forms.contact.pec")}
              required
              type="email"
              placeholder={t("forms.contact.pecPh")}
              hint={t("forms.contact.pecHint")}
              leftIcon={<Mail className="h-4 w-4" />}
              error={errors?.pec?.message}
            />
          </div>
        ) : null}
      </div>
    </ProfileSectionCard>
  );
}
